import React, { useEffect, useState } from 'react';
import { BarChart3, Users, Package, DollarSign, Activity, Sparkles, Share2 } from 'lucide-react';
import api from '../lib/api';
import { formatCurrency } from '../mock';
import { SalesReport } from './reports/SalesReport';
import { LeadsReport } from './reports/LeadsReport';
import { AgingReport } from './reports/AgingReport';
import { ProfitReport } from './reports/ProfitReport';
import { AiAssistant } from './reports/AiAssistant';
import { PlatformsReport } from './reports/PlatformsReport';
import { Ribbon, DataTable } from './reports/shared';

const fmtIdle = (s) => {
  const m = Math.floor((s || 0) / 60);
  return m < 60 ? `${m}min` : m < 1440 ? `${Math.floor(m / 60)}h ${m % 60}min` : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;
};

const Operations = () => {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get('/reports/summary'); setD(r.data); } catch (e) {} })(); }, []);
  if (!d) return <p className="ent-empty">Carregando indicadores…</p>;
  return (
    <div className="space-y-3" data-testid="ops-report">
      <Ribbon testid="ops-kpis" items={[
        ['Veículos em estoque', d.inventoryCount],
        ['Valor investido', formatCurrency(d.inventoryValue)],
        ['Unidades vendidas', d.unitsSold],
        ['Receita', formatCurrency(d.revenue)],
        ['Gross profit', formatCurrency(d.grossProfit), 't-ok'],
        ['Gross médio', formatCurrency(d.avgGross)],
        ['Leads totais', d.leadsTotal],
        ['Close rate', `${d.closeRate}%`],
        ['Aplicações (CRM)', d.applications ?? 0],
        ['Appointments', d.appointments],
        ['Show rate', `${d.showRate}%`],
        ['Leads perdidos', d.leadsLost, 't-warn'],
      ]} />
      <div className="ent-panel">
        <div className="ent-panel-head"><span className="ent-label">Tempo de resposta no CRM (por vendedor)</span></div>
        <DataTable testid="crm-idle-report" rowKey="vendedor" rows={d.crmIdle || []} empty="Sem movimentações de leads registradas ainda" cols={[
          { key: 'vendedor', label: 'VENDEDOR', render: (r) => <span className="font-semibold">{r.vendedor}</span> },
          { key: 'moves', label: 'MOVIMENTAÇÕES', align: 'right' },
          { key: 'aplicacoes', label: 'APLICAÇÕES', align: 'right', render: (r) => <span className="t-ok font-bold">{r.aplicacoes}</span> },
          { key: 'avg_idle_seconds', label: 'MÉDIA SEM INTERAÇÃO', align: 'right', render: (r) => <span className={r.avg_idle_seconds > 86400 ? 't-bad' : r.avg_idle_seconds > 3600 ? 't-warn' : ''}>{fmtIdle(r.avg_idle_seconds)}</span> },
          { key: 'max_idle_seconds', label: 'MAIOR TEMPO PARADO', align: 'right', render: (r) => fmtIdle(r.max_idle_seconds) },
        ]} />
      </div>
    </div>
  );
};

const Reports = () => {
  const [opts, setOpts] = useState(null);
  const [tab, setTab] = useState('ai');
  useEffect(() => { (async () => { try { const { data } = await api.get('/reports/filters'); setOpts(data); } catch (e) { setOpts({ is_manager: false }); } })(); }, []);
  if (!opts) return <p className="ent-empty">Carregando relatórios…</p>;

  const mgr = opts.is_manager;
  const tabs = mgr
    ? [['ai', 'Assistente IA', Sparkles], ['sales', 'Vendas', BarChart3], ['leads', 'Leads', Users], ['platforms', 'Plataformas', Share2], ['aging', 'Estoque · Aging', Package],
       ...(opts.fin_view ? [['profit', 'Profit F&I', DollarSign]] : []), ['ops', 'Operação', Activity]]
    : [['ai', 'Assistente IA', Sparkles], ['sales', 'Minhas vendas', BarChart3], ['leads', 'Meus leads', Users], ['platforms', 'Plataformas', Share2], ['aging', 'Estoque · Aging', Package]];

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div>
        <p className="ent-label">Business Intelligence</p>
        <h1 className="font-display text-2xl font-black uppercase tracking-tight">Relatórios{mgr ? '' : ` · ${opts.me}`}</h1>
      </div>

      <div className="ent-tabs" data-testid="reports-tabs">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} data-testid={`reports-tab-${k}`} onClick={() => setTab(k)} className={`ent-tab ${tab === k ? 'ent-tab-active' : ''}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      {tab === 'ai' && <AiAssistant />}
      {tab === 'sales' && <SalesReport opts={opts} mine={!mgr} />}
      {tab === 'leads' && <LeadsReport opts={opts} mine={!mgr} />}
      {tab === 'platforms' && <PlatformsReport />}
      {tab === 'aging' && <AgingReport />}
      {tab === 'profit' && <ProfitReport />}
      {tab === 'ops' && <Operations />}
    </div>
  );
};

export default Reports;
